import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import StarRating from "../Helper/StarRating";
import {
  addReviewApi,
  fetchReviews,
  deleteReview,
} from "../features/reviewSlice";
import { toast } from "react-toastify";

const ReviewSection = ({ productId }) => {
  const dispatch = useDispatch();
  const [sortBy, setSortBy] = useState("newest");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 0,
    comment: "",
  });
  const reviewsPerPage = 5;

  // Get user from localStorage
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;

  const reviews = useSelector(
    (state) => state.reviews?.reviews?.products || []
  );

  // Filter and sort reviews
  const filteredAndSortedReviews = useMemo(() => {
    let filtered = [...reviews];

    // Apply rating filter
    if (ratingFilter !== "all") {
      filtered = filtered.filter(
        (review) => review.rating === parseInt(ratingFilter)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === "newest") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortBy === "oldest") {
        return new Date(a.createdAt) - new Date(b.createdAt);
      } else if (sortBy === "highest") {
        return b.rating - a.rating;
      } else if (sortBy === "lowest") {
        return a.rating - b.rating;
      }
      return 0;
    });

    return filtered;
  }, [reviews, sortBy, ratingFilter]);

  // Get current reviews for pagination
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = filteredAndSortedReviews.slice(
    indexOfFirstReview,
    indexOfLastReview
  );
  const totalPages = Math.ceil(
    filteredAndSortedReviews.length / reviewsPerPage
  );

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    if (productId) {
      dispatch(fetchReviews(productId));
    }
  }, [dispatch, productId]);

  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [sortBy, ratingFilter]);

  const handleSubmitReview = async () => {
    if (!user) {
      toast.error("Please login to add a review");
      return;
    }

    if (newReview.rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    if (!newReview.comment.trim()) {
      toast.error("Please write a review comment");
      return;
    }

    try {
      await dispatch(
        addReviewApi(productId, newReview.rating, newReview.comment)
      );
      setShowReviewForm(false);
      setNewReview({ rating: 0, comment: "" });
    } catch (error) {
      console.error("Submit review error:", error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm("Are you sure you want to delete this review?")) {
      await dispatch(deleteReview(reviewId, productId));
    }
  };

  return (
    <div className="review-section mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 style={{ color: "#3c3260" }}>Reviews</h3>
        <button
          className="btn btn-primary"
          onClick={() => setShowReviewForm(true)}
          style={{ background: "#3c3260" }}
        >
          Write a Review
        </button>
      </div>

      {/* Filters */}
      <div className="filters d-flex gap-3 mb-4">
        <select
          className="form-select w-auto"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="highest">Highest Rating</option>
          <option value="lowest">Lowest Rating</option>
        </select>

        <select
          className="form-select w-auto"
          value={ratingFilter}
          onChange={(e) => setRatingFilter(e.target.value)}
        >
          <option value="all">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </select>
      </div>

      {/* Review Form */}
      {showReviewForm && (
        <div className="review-form mb-4 p-4 border rounded">
          <h5 className="mb-3" style={{ color: "#3c3260" }}>
            Write Your Review
          </h5>
          <div className="mb-3">
            <label className="form-label">Rating</label>
            <div className="rating-input">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className={`btn btn-link p-0 me-1 ${
                    newReview.rating >= star ? "text-warning" : "text-muted"
                  }`}
                  onClick={() => setNewReview({ ...newReview, rating: star })}
                >
                  <i className="fas fa-star"></i>
                </button>
              ))}
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Your Review</label>
            <textarea
              className="form-control"
              rows="3"
              value={newReview.comment}
              maxLength={2000}
              onChange={(e) =>
                setNewReview({ ...newReview, comment: e.target.value })
              }
              placeholder="Write your review here..."
            ></textarea>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary" onClick={handleSubmitReview}>
              Submit Review
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                setShowReviewForm(false);
                setNewReview({ rating: 0, comment: "" });
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Reviews List */}
      <div className="reviews-list">
        {currentReviews.length === 0 ? (
          <p className="text-center text-muted">No reviews found.</p>
        ) : (
          currentReviews.map((review) => (
            <div key={review._id} className="review-item p-3 border-bottom">
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="mb-1">
                    {`${review.userId?.firstName || ""} ${
                      review.userId?.lastName || ""
                    }`}
                  </h6>
                  <StarRating rating={review.rating} />
                </div>
                <div className="d-flex align-items-start gap-2">
                  <small className="text-muted">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </small>
                  {user && user._id === review.userId?._id && (
                    <button
                      className="btn btn-link text-danger p-0"
                      onClick={() => handleDeleteReview(review._id)}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-2 mb-0">{review.description}</p>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
            </li>
            {[...Array(totalPages)].map((_, index) => (
              <li
                key={index + 1}
                className={`page-item ${
                  currentPage === index + 1 ? "active" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => paginate(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default ReviewSection;
